import React, { useState, useEffect } from 'react';
import { X, FileText, CheckCircle2, AlertCircle, RefreshCw, Send, ShieldCheck, Receipt, Landmark, Search } from 'lucide-react';
import { fiscalService } from '../../services/fiscalService';
import { EmpresaFiscal } from '../../types/fiscal';
import { Conta } from '../../types/financeiro';
import { supabase } from '../../lib/supabase';
import { MunicipioUfSelect } from '../common/MunicipioUfSelect';
import { buscarDadosCnpj } from '../../lib/cnpjLookup';

// Vários cadastros têm mais de um endereço sem nenhum marcado como
// "principal" — pedir só 1 com order+limit nesse caso devolve qualquer um
// dos dois (inclusive um sem CEP preenchido, o que reproduziria o mesmo erro
// de CEP ausente na emissão). Busca todos e prioriza o principal com CEP,
// depois qualquer um com CEP, só then cai para o primeiro.
function escolherMelhorEndereco<T extends { principal?: string | null; cep?: string | null }>(enderecos: T[]): T | undefined {
  return enderecos.find((e) => e.principal === 'S' && e.cep)
    || enderecos.find((e) => !!e.cep)
    || enderecos[0];
}

interface EmitirNfseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialMedicao?: any; // Se aberto a partir de uma medição
  initialConta?: Conta | null; // Se aberto a partir do Financeiro (Contas a Receber)
}

export function EmitirNfseModal({ isOpen, onClose, onSuccess, initialMedicao, initialConta }: EmitirNfseModalProps) {
  const [empresas, setEmpresas] = useState<EmpresaFiscal[]>([]);
  const [empresaId, setEmpresaId] = useState<number>(1);
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaFiscal | null>(null);

  // Medições aprovadas
  const [medicoesList, setMedicoesList] = useState<any[]>([]);
  const [selectedMedicaoId, setSelectedMedicaoId] = useState<string>(
    initialMedicao ? String(initialMedicao.id) : initialConta?.medicoes_id ? String(initialConta.medicoes_id) : ''
  );
  const [currentMedicao, setCurrentMedicao] = useState<any>(initialMedicao || null);

  // Dados do Tomador (Cliente)
  const [tomadorNome, setTomadorNome] = useState('');
  const [tomadorCnpj, setTomadorCnpj] = useState('');
  const [tomadorEmail, setTomadorEmail] = useState('');
  const [tomadorCidade, setTomadorCidade] = useState('');
  const [tomadorUf, setTomadorUf] = useState('');
  const [tomadorCodigoIbge, setTomadorCodigoIbge] = useState('');
  const [tomadorCep, setTomadorCep] = useState('');
  const [buscandoCnpjTomador, setBuscandoCnpjTomador] = useState(false);

  // Serviço e Valores
  const [descricaoServico, setDescricaoServico] = useState('Prestação de serviços de transporte e logística');
  const [valorServico, setValorServico] = useState<number>(1000);
  const [aliquotaIss, setAliquotaIss] = useState<number>(2.0);
  const [codigoServico, setCodigoServico] = useState('16.01');
  const [issRetido, setIssRetido] = useState(false);

  // Status de Envio
  const [loading, setLoading] = useState(false);
  const [loadingMedicoes, setLoadingMedicoes] = useState(false);
  const [resultado, setResultado] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setResultado(null);
      setErro(null);
      carregarEmpresas();
      if (initialConta) {
        aplicarDadosConta(initialConta);
      } else if (initialMedicao) {
        aplicarDadosMedicao(initialMedicao);
      } else {
        carregarMedicoesAprovadas();
      }
    }
  }, [isOpen, initialMedicao, initialConta]);

  const carregarEmpresas = async () => {
    const list = await fiscalService.getEmpresasFiscais();
    setEmpresas(list);
    if (list.length > 0) {
      setEmpresaId(list[0].id);
      setSelectedEmpresa(list[0]);
    }
  };

  const carregarMedicoesAprovadas = async () => {
    try {
      setLoadingMedicoes(true);
      const { data } = await supabase
        .from('medicoes')
        .select('*')
        .in('status_medicao_id', [2, 4])
        .order('id', { ascending: false })
        .limit(25);

      const medicoes = data || [];

      // `medicoes` não tem cliente_nome — resolve em lote via
      // ordem_de_servico -> pedido -> pessoa (mesmo caminho de aplicarDadosMedicao)
      // só para exibir o nome certo no seletor.
      const osIds = Array.from(new Set(medicoes.map((m: any) => m.ordem_de_servico_id).filter(Boolean)));
      const nomePorMedicaoId = new Map<number, string>();
      if (osIds.length > 0) {
        const { data: ordens } = await supabase
          .from('ordem_de_servico')
          .select('id, pedido_id')
          .in('id', osIds);

        const pedidoIds = Array.from(new Set((ordens || []).map((o: any) => o.pedido_id).filter(Boolean)));
        const pedidoPorOsId = new Map<number, number>();
        (ordens || []).forEach((o: any) => {
          if (o.pedido_id) pedidoPorOsId.set(o.id, o.pedido_id);
        });

        if (pedidoIds.length > 0) {
          const { data: pedidos } = await supabase
            .from('pedido')
            .select('id, pessoa_id')
            .in('id', pedidoIds);

          const pessoaPorPedidoId = new Map<number, number>();
          (pedidos || []).forEach((p: any) => {
            if (p.pessoa_id) pessoaPorPedidoId.set(p.id, p.pessoa_id);
          });

          const pessoaIds = Array.from(new Set(Array.from(pessoaPorPedidoId.values())));
          if (pessoaIds.length > 0) {
            const { data: pessoas } = await supabase.from('pessoa').select('id, nome').in('id', pessoaIds);
            const nomePorPessoaId = new Map<number, string>();
            (pessoas || []).forEach((p: any) => nomePorPessoaId.set(p.id, p.nome));

            medicoes.forEach((m: any) => {
              const pedidoId = pedidoPorOsId.get(m.ordem_de_servico_id);
              const pessoaId = pedidoId ? pessoaPorPedidoId.get(pedidoId) : null;
              const nome = pessoaId ? nomePorPessoaId.get(pessoaId) : null;
              if (nome) nomePorMedicaoId.set(m.id, nome);
            });
          }
        }
      }

      setMedicoesList(medicoes.map((m: any) => ({ ...m, cliente_nome: nomePorMedicaoId.get(m.id) || null })));
    } catch (e) {
      console.warn('Erro ao carregar medições:', e);
    } finally {
      setLoadingMedicoes(false);
    }
  };

  const aplicarDadosConta = async (conta: Conta) => {
    const valor = Number(conta.valor) || 0;
    setValorServico(valor);

    let desc = conta.descricao
      ? `Prestação de serviços de transporte e logística vinculada ao Contas a Receber #${conta.id} - ${conta.descricao}`
      : `Prestação de serviços de transporte e logística - Contas a Receber #${conta.id}`;

    if (conta.medicoes_id) {
      desc += ` (Ref. Medição #${conta.medicoes_id})`;
      setSelectedMedicaoId(String(conta.medicoes_id));
      try {
        const { data: med } = await supabase
          .from('medicoes')
          .select('*')
          .eq('id', conta.medicoes_id)
          .single();
        if (med) {
          setCurrentMedicao(med);
          if (med.periodo_inicial || med.data_inicial) {
            desc += `. Período: ${med.periodo_inicial || med.data_inicial} a ${med.periodo_final || med.data_final || ''}`;
          }
        }
      } catch (mErr) {
        console.warn('Erro ao buscar medição vinculada:', mErr);
      }
    }
    setDescricaoServico(desc);

    // Buscar dados cadastrais do tomador/cliente
    if (conta.pessoa_id) {
      try {
        // Tabela é `pessoa` (singular) e não possui razao_social/cnpj_cpf/
        // cidade/uf — o endereço fica em `pessoa_endereco` -> `cidade` -> `estado`.
        const { data: pessoa } = await supabase
          .from('pessoa')
          .select('id, nome, nome_fantasia, documento, email')
          .eq('id', conta.pessoa_id)
          .maybeSingle();

        if (pessoa) {
          setTomadorNome(pessoa.nome || pessoa.nome_fantasia || 'Cliente Tomador');
          setTomadorCnpj(pessoa.documento || '');
          setTomadorEmail(pessoa.email || '');

          const { data: enderecos } = await supabase
            .from('pessoa_endereco')
            .select('cep, principal, cidade:cidade_id ( nome, codigo_ibge, estado:estado_id ( sigla ) )')
            .eq('pessoa_id', conta.pessoa_id);
          const endereco = escolherMelhorEndereco((enderecos || []) as any[]);

          const cidade = (endereco as any)?.cidade;
          if (cidade) {
            setTomadorCidade(cidade.nome || '');
            setTomadorUf((cidade.estado?.sigla || '').trim());
            // Código IBGE do município: exigido pela Focus NFe na NFS-e.
            setTomadorCodigoIbge(cidade.codigo_ibge || '');
          }
          if (endereco?.cep) setTomadorCep(endereco.cep);
        }
      } catch (pErr) {
        console.warn('Erro ao buscar cliente do contas a receber:', pErr);
      }
    }
  };

  const handleSelectMedicao = async (medIdStr: string) => {
    setSelectedMedicaoId(medIdStr);
    if (!medIdStr) {
      setCurrentMedicao(null);
      return;
    }
    const medId = Number(medIdStr);
    const { data } = await supabase
      .from('medicoes')
      .select('*')
      .eq('id', medId)
      .single();
    if (data) {
      setCurrentMedicao(data);
      aplicarDadosMedicao(data);
    }
  };

  // A tabela `medicoes` não guarda cliente_nome/cliente_cnpj/cliente_email
  // nem tem relação direta com `pedido` — o cliente é resolvido a partir de
  // ordem_de_servico_id -> ordem_de_servico.pedido_id -> pedido.pessoa_id ->
  // pessoa (mesmo caminho usado em aplicarDadosConta), e o endereço/CEP vem
  // de pessoa_endereco -> cidade -> estado.
  const aplicarDadosMedicao = async (med: any) => {
    const valor = Number(med.valor_medicao || 0);
    setValorServico(valor);

    const desc = `Prestação de serviços de transporte, movimentação e logística vinculada à Medição #${med.id}. Período: ${med.periodo_inicial || 'Início'} a ${med.periodo_final || 'Fim'}.`;
    setDescricaoServico(desc);

    try {
      let pessoaId: number | null = null;

      if (med.ordem_de_servico_id) {
        const { data: os } = await supabase
          .from('ordem_de_servico')
          .select('pedido_id')
          .eq('id', med.ordem_de_servico_id)
          .maybeSingle();

        if (os?.pedido_id) {
          const { data: pedido } = await supabase
            .from('pedido')
            .select('pessoa_id')
            .eq('id', os.pedido_id)
            .maybeSingle();
          pessoaId = pedido?.pessoa_id || null;
        }
      }

      if (pessoaId) {
        const { data: pessoa } = await supabase
          .from('pessoa')
          .select('id, nome, nome_fantasia, documento, email')
          .eq('id', pessoaId)
          .maybeSingle();

        if (pessoa) {
          setTomadorNome(pessoa.nome || pessoa.nome_fantasia || 'Cliente Tomador');
          setTomadorCnpj(pessoa.documento || '');
          setTomadorEmail(pessoa.email || '');
        }

        const { data: enderecos } = await supabase
          .from('pessoa_endereco')
          .select('cep, principal, cidade:cidade_id ( nome, codigo_ibge, estado:estado_id ( sigla ) )')
          .eq('pessoa_id', pessoaId);
        const endereco = escolherMelhorEndereco((enderecos || []) as any[]);

        const cidade = (endereco as any)?.cidade;
        if (cidade) {
          setTomadorCidade(cidade.nome || '');
          setTomadorUf((cidade.estado?.sigla || '').trim());
          setTomadorCodigoIbge(cidade.codigo_ibge || '');
        }
        if (endereco?.cep) setTomadorCep(endereco.cep);
      }
    } catch (err) {
      console.warn('Erro ao buscar cliente da medição:', err);
    }
  };

  const handleBuscarCnpjTomador = async () => {
    setBuscandoCnpjTomador(true);
    try {
      const dados = await buscarDadosCnpj(tomadorCnpj);
      if (!dados) {
        setErro('CNPJ não encontrado na Receita Federal / Focus NFe.');
        return;
      }
      setTomadorNome(dados.razaoSocial || tomadorNome);
      setTomadorCidade(dados.municipio || tomadorCidade);
      setTomadorUf(dados.uf || tomadorUf);
      setTomadorCodigoIbge(dados.codigoIbge || '');
      if (dados.email) setTomadorEmail(dados.email);
      if (dados.cep) setTomadorCep(dados.cep);
    } catch (err: any) {
      setErro(err.message || 'Erro ao consultar CNPJ.');
    } finally {
      setBuscandoCnpjTomador(false);
    }
  };

  const handleEmitir = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErro(null);
    setResultado(null);

    if (!selectedEmpresa?.codigo_municipio_ibge) {
      setErro('A empresa emissora selecionada não tem o Município de Registro configurado (aba Sistema > Configurações Fiscais). Esse dado é obrigatório para a NFS-e.');
      setLoading(false);
      return;
    }
    if (!tomadorCodigoIbge) {
      setErro('Selecione o município do tomador na lista (ou use "Buscar CNPJ") para obter o código IBGE correto.');
      setLoading(false);
      return;
    }
    const tomadorCepLimpo = tomadorCep.replace(/\D/g, '');
    if (tomadorCepLimpo.length !== 8) {
      setErro('Informe o CEP (8 dígitos) do tomador — a prefeitura exige esse campo para autorizar a NFS-e.');
      setLoading(false);
      return;
    }

    try {
      const docLimpo = tomadorCnpj.replace(/\D/g, '') || '';
      const isCpf = docLimpo.length <= 11;

      const payloadFocus = {
        data_emissao: new Date().toISOString(),
        natureza_operacao: '1',
        optante_simples_nacional: true,
        incentivador_cultural: false,

        prestador: {
          cnpj: selectedEmpresa?.cnpj?.replace(/\D/g, '') || '',
          inscricao_municipal: selectedEmpresa?.inscricao_municipal || '',
          codigo_municipio: selectedEmpresa.codigo_municipio_ibge,
        },

        tomador: {
          ...(isCpf && docLimpo ? { cpf: docLimpo } : {}),
          ...(!isCpf && docLimpo ? { cnpj: docLimpo } : {}),
          razao_social: tomadorNome || 'Cliente Tomador',
          email: tomadorEmail || undefined,
          endereco: {
            logradouro: 'Não Informado',
            numero: 'S/N',
            bairro: 'Centro',
            codigo_municipio: tomadorCodigoIbge,
            uf: tomadorUf || 'SP',
            cep: tomadorCepLimpo,
          }
        },

        servico: {
          aliquota: aliquotaIss,
          discriminacao: descricaoServico,
          iss_retido: issRetido,
          item_lista_servico: codigoServico,
          codigo_tributario_municipio: '1601001',
          valor_servicos: valorServico,
          codigo_municipio: selectedEmpresa.codigo_municipio_ibge,
        }
      };

      const res = await fiscalService.emitirNfse({
        empresa_id: empresaId,
        referencia: initialConta
          ? `NFSE-REC-${initialConta.id}-${Date.now().toString().slice(-4)}`
          : currentMedicao
          ? `NFSE-MED-${currentMedicao.id}-${Date.now().toString().slice(-4)}`
          : undefined,
        documento_fiscal: {
          valor_total: valorServico,
          valor_liquido: valorServico,
        },
        origem: initialConta
          ? { tipo: 'contas_receber', id: initialConta.id }
          : currentMedicao
          ? { tipo: 'medicoes', id: currentMedicao.id }
          : undefined,
        focus_payload: payloadFocus,
      });

      if (res.sucesso) {
        setResultado(res);

        // Se a emissão partiu do Contas a Receber, atualiza a conta vinculando o número da NF
        if (initialConta) {
          const numeroNota = String(res.numero || res.documento_id || '');
          const marcacao = `[NFS-e ${numeroNota} emitida com sucesso em ${new Date().toLocaleDateString('pt-BR')}]`;

          // Tabela é `conta` (singular) e não possui a coluna `nf_nome`.
          const { error: upErr } = await supabase
            .from('conta')
            .update({
              numero_nf: numeroNota || null,
              obs: initialConta.obs ? `${initialConta.obs}\n${marcacao}` : marcacao,
            })
            .eq('id', initialConta.id);

          if (upErr) {
            console.warn('Erro ao atualizar número da NFS-e na conta:', upErr.message);
          }
        }

        if (onSuccess) onSuccess();
      } else {
        setErro(res.mensagem || 'Falha ao emitir NFS-e na prefeitura.');
      }
    } catch (err: any) {
      setErro(err.message || 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Cabeçalho */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center text-white font-bold shadow-sm">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Emissão de NFS-e (Nota Fiscal de Serviços)</h2>
              <p className="text-xs text-slate-300">
                Módulo Financeiro • Emissão Direta para Contas a Receber
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Faixa Informativa se veio de Contas a Receber */}
        {initialConta && (
          <div className="bg-purple-50 border-b border-purple-200 px-6 py-2.5 flex items-center justify-between text-xs text-purple-900">
            <div className="flex items-center gap-2">
              <span className="font-bold bg-purple-200 text-purple-800 px-2 py-0.5 rounded text-[11px]">
                Contas a Receber #{initialConta.id}
              </span>
              <span>
                Valor: <strong>R$ {Number(initialConta.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
              </span>
              {initialConta.medicoes_id && (
                <span className="text-purple-700 bg-white border border-purple-200 px-2 py-0.5 rounded text-[10px] font-semibold">
                  Origem: Medição #{initialConta.medicoes_id}
                </span>
              )}
            </div>
            <span className="text-[11px] font-semibold text-purple-700">
              Vencimento: {initialConta.dt_vencimento || 'A definir'}
            </span>
          </div>
        )}

        {/* Formulário */}
        <form onSubmit={handleEmitir} className="p-6 space-y-5 overflow-y-auto flex-1 text-sm text-slate-800">
          {/* Sucesso */}
          {resultado && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-2 animate-in fade-in">
              <div className="flex items-center gap-2 font-bold text-emerald-800">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                NFS-e Emitida com Sucesso!
              </div>
              <p className="text-xs text-emerald-700">
                Referência: <span className="font-mono font-bold">{resultado.referencia}</span> | Número da Nota: <strong className="font-mono text-emerald-900">#{resultado.numero || resultado.documento_id}</strong>
              </p>
              <p className="text-xs text-emerald-600">
                {resultado.mensagem || 'Nota fiscal transmitida para a prefeitura e vinculada ao lançamento financeiro.'}
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold cursor-pointer"
                >
                  Concluir e Fechar
                </button>
              </div>
            </div>
          )}

          {/* Erro */}
          {erro && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-rose-800">Falha ao emitir NFS-e:</p>
                <p className="text-xs text-rose-700 mt-0.5">{erro}</p>
              </div>
            </div>
          )}

          {/* Empresa e Medição Vinculada */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Empresa Prestadora (Emissora):
              </label>
              <select
                value={empresaId}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setEmpresaId(id);
                  setSelectedEmpresa(empresas.find(emp => emp.id === id) || null);
                }}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-purple-500"
              >
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.razao_social} ({emp.cnpj})
                  </option>
                ))}
              </select>
              <div className="mt-1 flex items-center gap-1.5 text-2xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                Ambiente:{' '}
                <span className={`font-bold uppercase ${selectedEmpresa?.ambiente_emissao === 'producao' ? 'text-blue-600' : 'text-amber-600'}`}>
                  {selectedEmpresa?.ambiente_emissao || 'homologacao'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Vincular à Medição (Opcional):
              </label>
              <select
                value={selectedMedicaoId}
                onChange={(e) => handleSelectMedicao(e.target.value)}
                disabled={loadingMedicoes}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-purple-500 disabled:opacity-50"
              >
                <option value="">{initialConta?.medicoes_id ? `Medição #${initialConta.medicoes_id} (Vinculada à Conta)` : 'Nenhuma medição selecionada'}</option>
                {initialMedicao && (
                  <option value={String(initialMedicao.id)}>
                    Medição #{initialMedicao.id} - {initialMedicao.cliente_nome || 'Cliente'} (R$ {initialMedicao.valor_medicao || 0})
                  </option>
                )}
                {medicoesList.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    Medição #{m.id} - {m.cliente_nome || 'Cliente'} (R$ {m.valor_medicao || 0})
                  </option>
                ))}
              </select>
              <span className="text-2xs text-slate-400 mt-1 block">
                Vincula a medição original para rastreabilidade fiscal e contábil.
              </span>
            </div>
          </div>

          {/* Tomador do Serviço */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-blue-600" />
              Tomador do Serviço (Cliente Faturado)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-2xs text-slate-500">Razão Social / Nome:</label>
                <input
                  type="text"
                  value={tomadorNome}
                  onChange={(e) => setTomadorNome(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CNPJ / CPF:</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={tomadorCnpj}
                    onChange={(e) => setTomadorCnpj(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleBuscarCnpjTomador}
                    disabled={buscandoCnpjTomador}
                    title="Buscar dados na Receita Federal (via Focus NFe)"
                    className="shrink-0 px-2.5 bg-slate-900 hover:bg-black text-white rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {buscandoCnpjTomador ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Search className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-2xs text-slate-500">E-mail para Envio da Nota:</label>
                <input
                  type="email"
                  value={tomadorEmail}
                  onChange={(e) => setTomadorEmail(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CEP:</label>
                <input
                  type="text"
                  value={tomadorCep}
                  onChange={(e) => setTomadorCep(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
              <div className="sm:col-span-2">
                <MunicipioUfSelect
                  label="Município / UF do Tomador"
                  required
                  value={{ municipio: tomadorCidade, uf: tomadorUf, codigoIbge: tomadorCodigoIbge }}
                  onChange={(v) => {
                    setTomadorCidade(v.municipio);
                    setTomadorUf(v.uf);
                    setTomadorCodigoIbge(v.codigoIbge);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Discriminação do Serviço e Valores */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              Discriminação do Serviço & Impostos
            </h3>

            <div>
              <label className="block text-2xs text-slate-500 mb-1">Discriminação dos Serviços Prestados:</label>
              <textarea
                rows={3}
                value={descricaoServico}
                onChange={(e) => setDescricaoServico(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs leading-relaxed"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-2xs text-slate-500 font-bold text-blue-600">Valor dos Serviços (R$):</label>
                <input
                  type="number"
                  step="0.01"
                  value={valorServico}
                  onChange={(e) => setValorServico(Number(e.target.value))}
                  className="w-full bg-white border-2 border-blue-300 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-900"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Alíquota ISS (%):</label>
                <input
                  type="number"
                  step="0.1"
                  value={aliquotaIss}
                  onChange={(e) => setAliquotaIss(Number(e.target.value))}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Código Serviço (LC 116):</label>
                <input
                  type="text"
                  value={codigoServico}
                  onChange={(e) => setCodigoServico(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
              <div className="flex items-center pt-4">
                <label className="flex items-center gap-2 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={issRetido}
                    onChange={(e) => setIssRetido(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>ISS Retido na Fonte</span>
                </label>
              </div>
            </div>
          </div>
        </form>

        {/* Rodapé com Ações */}
        <div className="bg-slate-100 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold cursor-pointer"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleEmitir}
            disabled={loading}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-md shadow-blue-200 flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Transmitindo à Prefeitura...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Emitir NFS-e
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
